import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { CheckCircleIcon, DocumentArrowUpIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Layout from '@/components/Layout';
import { apiFetch } from '@/utils/api';

// Types for delivery note processing
interface DeliveryNoteTransaction {
    id: string;
    product: {
        id: string;
        name: string;
        is_draft: boolean;
    };
    quantity: number;
    unit_cost: number | null;
    expiry_date: string | null;
    status: string;
    notes: string | null;
}

interface DeliveryNoteBatch {
    id: string;
    document_number: string | null;
    supplier: {
        id: string | null;
        name: string | null;
    } | null;
    delivery_date: string | null;
    cost_center?: {
        id: string;
        name: string;
    };
    status: string;
    created_at: string;
}

interface DeliveryNoteData {
    batch: DeliveryNoteBatch;
    transactions: DeliveryNoteTransaction[];
}

interface CostCenter {
    id: string;
    name: string;
}

interface BatchItem {
    id: string; // Transaction ID from backend
    product_id: string;
    cost_center_id: string;
    quantity: number;
    unit_cost: number | null;
    expiry_date: string | null;
    status: string;
    notes: string | null;
    // Product info
    product_name: string;
    is_draft_product: boolean;
}

export default function ReceiveShipmentPage() {
    const navigate = useNavigate();
  const params = useParams({ strict: false }) as any;
  const routerState = useRouterState();
    const { batch_id } = params;

    // UI state
    const [currentStep, setCurrentStep] = useState<'upload' | 'review' | 'success'>('upload');
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [processingStatus, setProcessingStatus] = useState('');
    const [processingProgress, setProcessingProgress] = useState(0);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Delivery note data
    const [deliveryNoteData, setDeliveryNoteData] = useState<DeliveryNoteData | null>(null);
    const [batchId, setBatchId] = useState<string | null>(null);
    const [batchItems, setBatchItems] = useState<BatchItem[]>([]);

    // Reference data
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [selectedCostCenter, setSelectedCostCenter] = useState<string>('');
    const [loadingCostCenters, setLoadingCostCenters] = useState(true);

    // Form data
    const [shipmentReference, setShipmentReference] = useState('');

    useEffect(() => {
        fetchCostCenters();
    }, []);

    // Load existing batch if batch_id is provided in query params
    useEffect(() => {
        if (batch_id && typeof batch_id === 'string') {
            loadExistingBatch(batch_id);
        }
    }, [batch_id]);

    const fetchCostCenters = async () => {
        try {
            setLoadingCostCenters(true);
            const response = await apiFetch('/api/cost-centers/');

            if (response.ok) {
                const data = await response.json();
                setCostCenters(Array.isArray(data) ? data : []);
                // Auto-select first cost center if available
                if (data && data.length > 0) {
                    setSelectedCostCenter(data[0].id);
                }
            } else {
                console.error('Failed to fetch cost centers');
                setCostCenters([]);
            }
        } catch (error) {
            console.error('Error fetching cost centers:', error);
            setCostCenters([]);
        } finally {
            setLoadingCostCenters(false);
        }
    };

    const loadExistingBatch = async (batchId: string) => {
        try {
            setProcessing(true);
            setProcessingStatus('Loading delivery note...');

            const response = await apiFetch(`/api/delivery-notes/${batchId}`);
            if (response.ok) {
                const data: DeliveryNoteData = await response.json();

                // Set the batch data
                setBatchId(batchId);
                setDeliveryNoteData(data);
                setShipmentReference(data.batch.document_number || '');

                // Convert transactions to batch items for editing
                const items: BatchItem[] = data.transactions.map(transaction => ({
                    id: transaction.id,
                    product_id: transaction.product.id,
                    cost_center_id: data.batch.cost_center?.id || '',
                    quantity: transaction.quantity,
                    unit_cost: transaction.unit_cost || null,
                    expiry_date: transaction.expiry_date || null,
                    status: transaction.status,
                    notes: null,
                    product_name: transaction.product.name,
                    is_draft_product: transaction.product.is_draft || false
                }));

                setBatchItems(items);

                // Set cost center if available
                if (data.batch.cost_center) {
                    setSelectedCostCenter(data.batch.cost_center.id);
                }

                // Go directly to review step
                setCurrentStep('review');
                setProcessingStatus('');
                setProcessing(false);
            } else {
                throw new Error('Failed to load delivery note');
            }
        } catch (error: any) {
            console.error('Error loading existing batch:', error);
            setMessage({ type: 'error', text: error.message || 'Failed to load delivery note' });
            setProcessing(false);
            setProcessingStatus('');
        }
    };

    const handleFileSelect = (selectedFile: File | null) => {
        setFile(selectedFile);
        setMessage(null);
        // Reset processing state when new file is selected
        if (selectedFile) {
            setProcessing(false);
            setProcessingStatus('');
            setProcessingProgress(0);
        }
    };

    const waitForProcessing = async (batchId: string) => {
        let attempts = 0;
        const maxAttempts = 600; // Wait up to 2 minutes

        while (attempts < maxAttempts) {
            try {
                const response = await apiFetch(`/api/delivery-notes/${batchId}`);
                if (response.ok) {
                    const data: DeliveryNoteData = await response.json();

                    const status = data.batch.status?.toUpperCase();
                    if (status === 'READY_FOR_REVIEW') {
                        // Processing complete, convert to batch items
                        const items: BatchItem[] = data.transactions.map(transaction => ({
                            id: transaction.id,
                            product_id: transaction.product.id,
                            cost_center_id: data.batch.cost_center?.id || '',
                            quantity: transaction.quantity,
                            unit_cost: transaction.unit_cost,
                            expiry_date: transaction.expiry_date,
                            status: transaction.status,
                            notes: transaction.notes,
                            product_name: transaction.product.name,
                            is_draft_product: transaction.product.is_draft
                        }));

                        setBatchItems(items);
                        setDeliveryNoteData(data);
                        setShipmentReference(data.batch.document_number || '');

                        // Show completion
                        setProcessingStatus('Processing complete!');
                        setProcessingProgress(100);

                        // Brief pause to show completion
                        await new Promise(resolve => setTimeout(resolve, 500));

                        // Reset processing state
                        setProcessing(false);
                        setProcessingStatus('');
                        setProcessingProgress(0);

                        setCurrentStep('review');
                        return;
                    } else if (status === 'PROCESSING') {
                        // Still processing
                        const progressPercent = Math.min(30 + (attempts / 120) * 60, 90);
                        setProcessingProgress(progressPercent);
                        setProcessingStatus(`Processing document with AI... (${Math.floor(progressPercent)}%)`);
                    } else if (status === 'FAILED') {
                        throw new Error('Document processing failed');
                    }
                } else {
                    throw new Error('Failed to fetch processing status');
                }
            } catch (error) {
                console.error('Error checking processing status:', error);
            }

            attempts++;
            await new Promise(resolve => setTimeout(resolve, 2000));
        }

        throw new Error('Document processing timed out. Please try again.');
    };

    const handleUploadAndExtract = async () => {
        if (!file) {
            setMessage({ type: 'error', text: 'Please select a file' });
            return;
        }

        if (!selectedCostCenter) {
            setMessage({ type: 'error', text: 'Please select a delivery location' });
            return;
        }

        setUploading(true);
        setProcessing(true);
        setMessage(null);
        setProcessingStatus('Uploading delivery note...');
        setProcessingProgress(10);

        const formData = new FormData();
        formData.append('file', file);

        try {
            // Upload delivery note with selected cost center
            const uploadResponse = await apiFetch(`/api/delivery-notes/upload?cost_center_id=${selectedCostCenter}`, {
                method: 'POST',
                body: formData,
            });

            if (uploadResponse.ok) {
                const uploadResult = await uploadResponse.json();
                const batchId = uploadResult.batch_id;
                setBatchId(batchId);

                setProcessingStatus('Starting AI processing...');
                setProcessingProgress(20);

                // Start processing
                const processResponse = await apiFetch(`/api/delivery-notes/${batchId}/process`, {
                    method: 'POST',
                });

                if (processResponse.ok) {
                    setProcessingStatus('Processing document with AI...');
                    setProcessingProgress(30);

                    // Wait for processing to complete
                    await waitForProcessing(batchId);
                } else {
                    const errorData = await processResponse.json();
                    throw new Error(errorData.detail || 'Failed to start processing');
                }
            } else {
                const errorData = await uploadResponse.json();
                throw new Error(errorData.detail || 'Upload failed');
            }
        } catch (error: any) {
            console.error('Upload and processing failed:', error);
            setMessage({ type: 'error', text: error.message || 'Upload and processing failed' });
            setProcessing(false);
            setProcessingStatus('');
            setProcessingProgress(0);
        } finally {
            setUploading(false);
        }
    };

    const updateTransaction = async (transactionId: string, updates: Partial<BatchItem>) => {
        if (!batchId) return;

        try {
            const response = await apiFetch(`/api/delivery-notes/${batchId}/transactions/${transactionId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates)
            });

            if (response.ok) {
                // Update local state
                setBatchItems(items =>
                    items.map(item =>
                        item.id === transactionId ? { ...item, ...updates } : item
                    )
                );
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to update transaction');
            }
        } catch (error: any) {
            console.error('Failed to update transaction:', error);
            setMessage({ type: 'error', text: error.message || 'Failed to update transaction' });
        }
    };

    const handleApproveDeliveryNote = async () => {
        if (!batchId) return;

        try {
            const response = await apiFetch(`/api/delivery-notes/${batchId}/approve`, {
                method: 'POST',
            });

            if (response.ok) {
                const result = await response.json();
                setMessage({
                    type: 'success',
                    text: `Delivery note approved! ${result.transactions_count} items added to inventory.`
                });
                setCurrentStep('success');
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to approve delivery note');
            }
        } catch (error: any) {
            console.error('Failed to approve delivery note:', error);
            setMessage({ type: 'error', text: error.message || 'Failed to approve delivery note' });
        }
    };

    const renderUploadStep = () => (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-medium text-gray-900">Upload Delivery Note</h2>
                <p className="mt-1 text-sm text-gray-500">
                    Upload a delivery note PDF to automatically extract product information and create inventory batches
                </p>
            </div>

            {/* Cost Center Selector */}
            <div>
                <label htmlFor="cost-center" className="block text-sm font-medium text-gray-700 mb-2">
                    Delivery Location *
                </label>
                {loadingCostCenters ? (
                    <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm text-gray-500">Loading locations...</span>
                    </div>
                ) : (
                    <select
                        id="cost-center"
                        value={selectedCostCenter}
                        onChange={(e) => setSelectedCostCenter(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        required
                    >
                        <option value="">Select delivery location</option>
                        {costCenters.map(center => (
                            <option key={center.id} value={center.id}>
                                {center.name}
                            </option>
                        ))}
                    </select>
                )}
                <p className="mt-1 text-xs text-gray-500">
                    Where is this shipment being delivered?
                </p>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <div className="text-center">
                    <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="mt-4">
                        <label htmlFor="file-upload" className="cursor-pointer">
                            <span className="mt-2 block text-sm font-medium text-gray-900">
                                {file ? file.name : 'Choose delivery note PDF'}
                            </span>
                            <input
                                id="file-upload"
                                name="file-upload"
                                type="file"
                                accept=".pdf"
                                className="sr-only"
                                onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                            />
                        </label>
                        <p className="mt-1 text-xs text-gray-500">PDF up to 10MB</p>
                    </div>
                </div>
            </div>

            {file && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex">
                        <CheckCircleIcon className="h-5 w-5 text-blue-400" />
                        <div className="ml-3">
                            <p className="text-sm text-blue-800">
                                File selected: <strong>{file.name}</strong>
                            </p>
                            <p className="text-xs text-blue-600 mt-1">
                                Ready to process with AI for automatic data extraction
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {processing && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                        <div className="ml-3 flex-1">
                            <p className="text-sm font-medium text-yellow-800">{processingStatus}</p>
                            <div className="mt-2 bg-yellow-200 rounded-full h-2">
                                <div
                                    className="bg-yellow-600 h-2 rounded-full transition-all duration-50"
                                    style={{ width: `${processingProgress}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex justify-between">
                <button
                    type="button"
                    onClick={() => navigate({ to: '/inventory/batches' })}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleUploadAndExtract}
                    disabled={!file || !selectedCostCenter || uploading || processing || loadingCostCenters}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {uploading ? 'Uploading...' : processing ? 'Processing...' : 'Upload & Process'}
                </button>
            </div>
        </div>
    );

    const renderReviewStep = () => {
        return (
            <div className="space-y-6">
                <div>
                    <h2 className="text-lg font-medium text-gray-900">Review Extracted Data</h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Review and edit the extracted line items from the delivery note
                    </p>
                </div>

                {deliveryNoteData && (
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-sm font-medium text-gray-900 mb-2">Delivery Note Information</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-500">Document Number:</span>
                                <span className="ml-2 font-medium">{deliveryNoteData.batch.document_number || 'N/A'}</span>
                            </div>
                            <div>
                                <span className="text-gray-500">Supplier:</span>
                                <span className="ml-2 font-medium">{deliveryNoteData.batch.supplier?.name || 'N/A'}</span>
                            </div>
                            <div>
                                <span className="text-gray-500">Delivery Date:</span>
                                <span className="ml-2 font-medium">
                                    {deliveryNoteData.batch.delivery_date ?
                                        new Date(deliveryNoteData.batch.delivery_date).toLocaleDateString() : 'N/A'}
                                </span>
                            </div>
                            <div>
                                <span className="text-gray-500">Location:</span>
                                <span className="ml-2 font-medium">{deliveryNoteData.batch.cost_center?.name || 'No location assigned'}</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                    <div className="px-4 py-5 sm:p-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                            Line Items ({batchItems.length})
                        </h3>

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Product
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Quantity
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Unit Cost
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Expiry Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {batchItems.map((item) => (
                                        <tr key={item.id}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {item.product_name}
                                                        </div>
                                                        {item.is_draft_product && (
                                                            <div className="text-xs text-orange-600">
                                                                Draft Product - Needs Review
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <input
                                                    type="number"
                                                    value={item.quantity}
                                                    onChange={(e) => updateTransaction(item.id, { quantity: parseFloat(e.target.value) || 0 })}
                                                    className="w-20 px-2 py-1 text-sm border border-gray-300 rounded"
                                                    step="0.001"
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <input
                                                    type="number"
                                                    value={item.unit_cost || ''}
                                                    onChange={(e) => updateTransaction(item.id, { unit_cost: parseFloat(e.target.value) || null })}
                                                    className="w-24 px-2 py-1 text-sm border border-gray-300 rounded"
                                                    step="0.01"
                                                    placeholder="0.00"
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <input
                                                    type="date"
                                                    value={item.expiry_date ? item.expiry_date.split('T')[0] : ''}
                                                    onChange={(e) => updateTransaction(item.id, { expiry_date: e.target.value || null })}
                                                    className="px-2 py-1 text-sm border border-gray-300 rounded"
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                    item.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                                    item.status === 'CONFIRMED' ? 'bg-green-100 text-green-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {item.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="flex justify-between">
                    <button
                        type="button"
                        onClick={() => {
                            setCurrentStep('upload');
                            setFile(null);
                            setDeliveryNoteData(null);
                            setBatchItems([]);
                            setBatchId(null);
                            setMessage(null);
                            // Reset cost center to first available
                            if (costCenters.length > 0) {
                                setSelectedCostCenter(costCenters[0].id);
                            }
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        Start Over
                    </button>
                    <button
                        type="button"
                        onClick={handleApproveDeliveryNote}
                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                    >
                        Approve & Create Batches
                    </button>
                </div>
            </div>
        );
    };

    const renderSuccessStep = () => (
        <div className="text-center py-12">
            <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
            <h2 className="mt-4 text-lg font-medium text-gray-900">Delivery Note Processed Successfully!</h2>
            <p className="mt-2 text-sm text-gray-500">
                All items have been added to inventory and are ready for use.
            </p>
            <div className="mt-6 space-x-4">
                <button
                    type="button"
                    onClick={() => navigate({ to: '/inventory/batches' })}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700">
                    View Inventory Batches
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setCurrentStep('upload');
                        setFile(null);
                        setDeliveryNoteData(null);
                        setBatchItems([]);
                        setBatchId(null);
                        setMessage(null);
                        // Reset cost center to first available
                        if (costCenters.length > 0) {
                            setSelectedCostCenter(costCenters[0].id);
                        }
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                    Process Another Delivery Note
                </button>
            </div>
        </div>
    );

    return (
        <Layout>
            <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900">
                    {batch_id ? 'Review Delivery Note' : 'Receive Shipment'}
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                    {batch_id ?
                        'Review and approve the extracted product information' :
                        'Process delivery notes to automatically create inventory batches'
                    }
                </p>
            </div>

            {/* Progress Steps */}
            <div className="mb-8">
                <nav aria-label="Progress">
                    <ol className="flex items-center">
                        <li className={`relative ${currentStep === 'upload' ? 'text-blue-600' : currentStep === 'review' || currentStep === 'success' ? 'text-green-600' : 'text-gray-500'}`}>
                            <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                                currentStep === 'upload' ? 'border-blue-600 bg-blue-50' :
                                currentStep === 'review' || currentStep === 'success' ? 'border-green-600 bg-green-50' :
                                'border-gray-300 bg-white'
                            }`}>
                                <span className="text-sm font-medium">1</span>
                            </div>
                            <span className="ml-2 text-sm font-medium">Upload</span>
                        </li>
                        <li className={`relative ml-8 ${currentStep === 'review' ? 'text-blue-600' : currentStep === 'success' ? 'text-green-600' : 'text-gray-500'}`}>
                            <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                                currentStep === 'review' ? 'border-blue-600 bg-blue-50' :
                                currentStep === 'success' ? 'border-green-600 bg-green-50' :
                                'border-gray-300 bg-white'
                            }`}>
                                <span className="text-sm font-medium">2</span>
                            </div>
                            <span className="ml-2 text-sm font-medium">Review</span>
                        </li>
                        <li className={`relative ml-8 ${currentStep === 'success' ? 'text-green-600' : 'text-gray-500'}`}>
                            <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                                currentStep === 'success' ? 'border-green-600 bg-green-50' : 'border-gray-300 bg-white'
                            }`}>
                                <span className="text-sm font-medium">3</span>
                            </div>
                            <span className="ml-2 text-sm font-medium">Complete</span>
                        </li>
                    </ol>
                </nav>
            </div>

            {/* Message Display */}
            {message && (
                <div className={`mb-6 rounded-md p-4 ${
                    message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                }`}>
                    <div className="flex">
                        {message.type === 'success' ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-400" />
                        ) : (
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                        )}
                        <div className="ml-3">
                            <p className={`text-sm ${message.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                                {message.text}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Step Content */}
            <div className="bg-white shadow rounded-lg p-6">
                {currentStep === 'upload' && renderUploadStep()}
                {currentStep === 'review' && renderReviewStep()}
                {currentStep === 'success' && renderSuccessStep()}
            </div>
        </div>
        </Layout>
    );
}

export const Route = createFileRoute('/inventory/batches/receive-shipment')({
  component: ReceiveShipmentPage,
})
